/**
 * ArquivosAuditoriaPage — as consultas de preço guardadas de UM processo.
 *
 * Réplica do super_admin_arquivos_licitacao.html. Cada linha é uma consulta
 * ao catálogo, publicada como .json.gz sob /static/licitacoes para
 * conferência externa (o hash vai no relatório do ETP).
 *
 * A tela antiga dizia "arquivo ausente" para todos: procurava
 * `{codigo}.json.gz`, e o nome escrito por export_manager.py é
 * `catmat-{codigo}-{hash[:16]}.json.gz`.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faDownload,
  faFileZipper,
  faSpinner,
  faTrashAlt,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  arquivosDeAuditoria,
  excluirArquivosDeAuditoria,
} from '../../api/superAdmin'
import { useToast } from '../../components/Toast'

function tamanho(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function ArquivosAuditoriaPage() {
  const { processoId } = useParams()
  const id = Number(processoId)
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [marcados, setMarcados] = useState<number[]>([])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['super-admin', 'auditoria-precos', id],
    queryFn: () => arquivosDeAuditoria(id),
    retry: false,
  })

  const excluir = useMutation({
    mutationFn: () => excluirArquivosDeAuditoria(marcados),
    onSuccess: (r) => {
      notificar(
        'success',
        `${r.arquivos} consulta(s) removidas · ${r.arquivos_no_disco} arquivo(s) apagados do disco.`,
      )
      setMarcados([])
      void clienteQuery.invalidateQueries({ queryKey: ['super-admin'] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="p-8 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando as consultas…
      </p>
    )
  }
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-dark-card">
        <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Processo não encontrado
        </h1>
        <Link to="/super-admin/auditorias" className="mt-4 inline-block text-purple-600">
          Voltar
        </Link>
      </div>
    )
  }

  const total = data.arquivos.reduce((soma, a) => soma + a.bytes, 0)
  const ausentes = data.arquivos.filter((a) => !a.existe_no_disco).length

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-900 p-6 text-white">
          <Link
            to="/super-admin/auditorias"
            className="mb-2 inline-flex items-center gap-2 text-sm text-purple-200 hover:text-white"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Auditoria de preços
          </Link>
          <h1 className="text-2xl font-bold">
            Processo #{data.processo_id}
            {data.numero_processo && ` · ${data.numero_processo}`}
          </h1>
          <p className="mt-1 text-purple-200">{data.objeto}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-4">
          {[
            ['Consultas', String(data.arquivos.length)],
            ['Espaço', tamanho(total)],
            ['Sem arquivo no disco', String(ausentes)],
            ['Município', data.municipio || '—'],
          ].map(([rotulo, valor]) => (
            <div key={rotulo}>
              <p className="text-xs uppercase text-slate-500">{rotulo}</p>
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
                {valor}
              </p>
            </div>
          ))}
        </div>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() =>
              setMarcados(
                marcados.length === data.arquivos.length
                  ? []
                  : data.arquivos.map((a) => a.id),
              )
            }
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
          >
            {marcados.length === data.arquivos.length
              ? 'Desmarcar todos'
              : 'Marcar todos'}
          </button>
          <button
            type="button"
            disabled={!marcados.length || excluir.isPending}
            onClick={() => {
              if (confirm(`Excluir ${marcados.length} consulta(s)?`))
                excluir.mutate()
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            <FontAwesomeIcon
              icon={excluir.isPending ? faSpinner : faTrashAlt}
              spin={excluir.isPending}
            />
            Excluir selecionadas
          </button>
          <span className="text-xs text-slate-500">
            Some do banco e do disco — o documento gerado não é afetado.
          </span>
        </div>

        {data.arquivos.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhuma consulta guardada para este processo.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                  <th className="py-2 pr-4" />
                  <th className="py-2 pr-4">Item do catálogo</th>
                  <th className="py-2 pr-4">Registros</th>
                  <th className="py-2 pr-4">Tamanho</th>
                  <th className="py-2 pr-4">Consulta</th>
                  <th className="py-2">Arquivo</th>
                </tr>
              </thead>
              <tbody>
                {data.arquivos.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox"
                        aria-label={`Selecionar consulta ${a.codigo_catmat}`}
                        checked={marcados.includes(a.id)}
                        onChange={(e) =>
                          setMarcados(
                            e.target.checked
                              ? [...marcados, a.id]
                              : marcados.filter((x) => x !== a.id),
                          )
                        }
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <span className="block font-medium text-slate-800 dark:text-slate-100">
                        {a.codigo_catmat} — {a.descricao}
                      </span>
                      {(a.grupo || a.classe) && (
                        <span className="block text-xs text-slate-500">
                          {[a.grupo, a.classe].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {a.registros}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {tamanho(a.bytes)}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {a.consultado_em}
                    </td>
                    <td className="py-2">
                      {a.existe_no_disco ? (
                        <a
                          href={`/static/licitacoes/${data.pasta.split('/static/licitacoes/')[1]}/${a.arquivo}`}
                          className="inline-flex items-center gap-2 text-emerald-600 hover:underline dark:text-emerald-400"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span className="text-xs">{a.hash}…</span>
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          só no banco
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
          <FontAwesomeIcon icon={faFileZipper} />
          Pasta publicada: <code>{data.pasta}</code>
          {ausentes === 0 && data.arquivos.length > 0 && (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <FontAwesomeIcon icon={faCheckCircle} />
              todos os arquivos presentes
            </span>
          )}
        </p>
      </section>
    </div>
  )
}
